import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDealData } from "./DealContext";
import { usePortfolioData } from "./PortfolioContext";
import { useViewMode } from "./ViewModeContext";

// Create context
const TabContext = createContext();

// Custom hook to use the context
export const useTabs = () => useContext(TabContext);

export const TabProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentDealName, isDealLoading } = useDealData();
  const { currentPortfolioName, isPortfolioLoading } = usePortfolioData();
  const { viewMode } = useViewMode();

  // View-mode-aware variables
  const isPortfolioMode = viewMode === "portfolio";
  const activeCurrentName = isPortfolioMode
    ? currentPortfolioName
    : currentDealName;
  const activeIsLoading = isPortfolioMode ? isPortfolioLoading : isDealLoading;

  // State for dynamic tabs
  const [dynamicTabs, setDynamicTabs] = useState([]);
  const [activeTab, setActiveTab] = useState("");
  const [maintainingTab, setMaintainingTab] = useState(false); // Flag to prevent flashes

  // Use refs to track previous values without triggering re-renders
  const lastDealNameRef = useRef(null);
  const lastPortfolioNameRef = useRef(null);
  const lastViewModeRef = useRef(viewMode);
  const isHandlingEntityChangeRef = useRef(false);
  const pendingTabChangeRef = useRef(null);
  const lastPathRef = useRef(location.pathname);

  // Effect to handle location changes and prevent flashes
  useEffect(() => {
    // Store the current path
    const currentPath = location.pathname;

    // Update path reference
    lastPathRef.current = currentPath;

    // If we're already on a chart page and we're loading,
    // set the maintainingTab flag to prevent navigation to overview
    if (
      (currentPath.includes("/finance-charts/") ||
        currentPath.includes("/competition-charts/") ||
        currentPath.includes("/financial-model")) &&
      activeIsLoading
    ) {
      setMaintainingTab(true);
    } else if (maintainingTab && !activeIsLoading) {
      // Reset the flag once loading is complete
      setTimeout(() => {
        setMaintainingTab(false);
      }, 300);
    }
  }, [location.pathname, activeIsLoading, maintainingTab]);

  // Effect to handle deal/portfolio changes and preserve tabs
  useEffect(() => {
    // Skip if we're still loading
    if (activeIsLoading) {
      return;
    }

    const previousDealName = lastDealNameRef.current;
    const previousPortfolioName = lastPortfolioNameRef.current;
    lastDealNameRef.current = currentDealName;
    lastPortfolioNameRef.current = currentPortfolioName;

    // Check if there was an entity change (deal or portfolio)
    const dealChanged =
      currentDealName &&
      previousDealName &&
      currentDealName !== previousDealName;
    const portfolioChanged =
      currentPortfolioName &&
      previousPortfolioName &&
      currentPortfolioName !== previousPortfolioName;

    if (dealChanged || portfolioChanged) {
      // Update ALL tabs to reflect the new entity (deal or portfolio), not just the active one
      setDynamicTabs((prev) => {
        const updatedTabs = prev.map((tab) => {
          // Handle financial model tab separately (different URL structure)
          if (tab.id === 'financial-model') {
            let newPath;
            if (isPortfolioMode && currentPortfolioName) {
              newPath = `/portfolio/${encodeURIComponent(currentPortfolioName)}/financial-model`;
            } else if (!isPortfolioMode && currentDealName) {
              newPath = `/deals/${encodeURIComponent(currentDealName)}/financial-model`;
            } else {
              newPath = `/financial-model`;
            }
            return { ...tab, path: newPath };
          }

          // Determine chart type based on current path
          const chartType = tab.path.includes("/finance-charts/")
            ? "finance-charts"
            : "competition-charts";

          // Create path based on current view mode
          let newPath;
          if (isPortfolioMode && currentPortfolioName) {
            newPath = `/portfolio/${encodeURIComponent(
              currentPortfolioName
            )}/${chartType}/${tab.id}`;
          } else if (!isPortfolioMode && currentDealName) {
            newPath = `/deals/${encodeURIComponent(
              currentDealName
            )}/${chartType}/${tab.id}`;
          } else {
            newPath = `/${chartType}/${tab.id}`;
          }

          return { ...tab, path: newPath };
        });
        return updatedTabs;
      });

      // If we have an active tab, update the URL to reflect the new entity
      if (activeTab && dynamicTabs.find((tab) => tab.id === activeTab)) {
        // If we're already in the middle of handling an entity change, store this as pending
        if (isHandlingEntityChangeRef.current) {
          pendingTabChangeRef.current = activeTab;
          return;
        }

        // Mark that we're handling an entity change
        isHandlingEntityChangeRef.current = true;

        // Update the URL using replaceState to avoid triggering navigation events
        let newPath;

        // Handle financial model tab separately
        if (activeTab === 'financial-model') {
          if (isPortfolioMode && currentPortfolioName) {
            newPath = `/portfolio/${encodeURIComponent(currentPortfolioName)}/financial-model`;
          } else if (!isPortfolioMode && currentDealName) {
            newPath = `/deals/${encodeURIComponent(currentDealName)}/financial-model`;
          } else {
            newPath = `/financial-model`;
          }
        } else {
          // Determine chart type for the active tab
          const activeTabData = dynamicTabs.find((tab) => tab.id === activeTab);
          const chartType = activeTabData?.path?.includes("/finance-charts/")
            ? "finance-charts"
            : "competition-charts";

          if (isPortfolioMode && currentPortfolioName) {
            newPath = `/portfolio/${encodeURIComponent(
              currentPortfolioName
            )}/${chartType}/${activeTab}`;
          } else if (!isPortfolioMode && currentDealName) {
            newPath = `/deals/${encodeURIComponent(
              currentDealName
            )}/${chartType}/${activeTab}`;
          } else {
            newPath = `/${chartType}/${activeTab}`;
          }
        }

        // Use replaceState instead of navigate to avoid race conditions
        window.history.replaceState(null, "", newPath);

        // Reset flags after a delay
        setTimeout(() => {
          isHandlingEntityChangeRef.current = false;
          pendingTabChangeRef.current = null;
          setMaintainingTab(false);
        }, 500);
      }
    }
  }, [
    currentDealName,
    currentPortfolioName,
    activeIsLoading,
    activeTab,
    isPortfolioMode,
  ]);

  // Effect to handle view mode changes and update existing tabs
  useEffect(() => {
    const previousViewMode = lastViewModeRef.current;
    lastViewModeRef.current = viewMode;

    // Check if view mode changed
    if (previousViewMode !== viewMode && dynamicTabs.length > 0) {
      // Helper to build a tab path for the current view mode
      const buildTabPath = (tab) => {
        // Financial model tab has a different URL structure
        if (tab.id === 'financial-model') {
          if (isPortfolioMode && currentPortfolioName) {
            return `/portfolio/${encodeURIComponent(currentPortfolioName)}/financial-model`;
          } else if (!isPortfolioMode && currentDealName) {
            return `/deals/${encodeURIComponent(currentDealName)}/financial-model`;
          }
          return `/financial-model`;
        }

        const chartType = tab.path.includes("/finance-charts/")
          ? "finance-charts"
          : "competition-charts";

        if (isPortfolioMode && currentPortfolioName) {
          return `/portfolio/${encodeURIComponent(currentPortfolioName)}/${chartType}/${tab.id}`;
        } else if (!isPortfolioMode && currentDealName) {
          return `/deals/${encodeURIComponent(currentDealName)}/${chartType}/${tab.id}`;
        }
        return `/${chartType}/${tab.id}`;
      };

      // Update all existing tabs to use the new view mode's URL structure
      setDynamicTabs((prev) => {
        return prev.map((tab) => ({ ...tab, path: buildTabPath(tab) }));
      });

      // If there's an active tab, navigate to its new URL with a small delay
      // to allow any existing map components to clean up properly
      if (activeTab) {
        const activeTabData = dynamicTabs.find((tab) => tab.id === activeTab);
        if (activeTabData) {
          navigate(buildTabPath(activeTabData));
        }
      }
    }
  }, [
    viewMode,
    isPortfolioMode,
    currentPortfolioName,
    currentDealName,
    dynamicTabs,
    activeTab,
    navigate,
  ]);

  // Effect to set the active tab based on URL
  useEffect(() => {
    // Skip this effect if we're maintaining tab during deal change
    if (maintainingTab) {
      return;
    }

    // Normalize away any trailing slash so a URL like `.../finance-charts/trends/`
    // doesn't produce a tab titled "Trends/" (which then contaminates every
    // subsequent URL rebuild via tab.id).
    const currentPath = location.pathname.replace(/\/+$/, "");

    // Check if current path matches any dynamic tab
    const matchedTab = dynamicTabs.find((tab) => tab.path === currentPath);
    if (matchedTab) {
      setActiveTab(matchedTab.id);
    } else if (currentPath.endsWith("/financial-model")) {
      // If we're on the financial model page but it's not in our tabs (e.g. after refresh)
      if (!dynamicTabs.find((tab) => tab.id === 'financial-model')) {
        addTab('financial-model', 'Financial Model', currentPath);
      }
    } else if (
      currentPath.includes("/finance-charts/") ||
      currentPath.includes("/competition-charts/")
    ) {
      // If we're on a chart page but it's not in our tabs (e.g. after refresh)
      // Extract the chart type and ID from the URL. Only take the first segment
      // after the chart-type marker — the route is `:chartType`, not a nested path.
      let chartId;
      let chartType;

      if (currentPath.includes("/finance-charts/")) {
        chartType = "finance";
        chartId = currentPath.split("/finance-charts/")[1]?.split("/")[0];
      } else if (currentPath.includes("/competition-charts/")) {
        chartType = "competition";
        chartId = currentPath.split("/competition-charts/")[1]?.split("/")[0];
      }

      if (chartId && !dynamicTabs.find((tab) => tab.id === chartId)) {
        // Re-add the tab
        const tabTitle = chartId
          .split("-")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");

        addTab(chartId, tabTitle, currentPath);
      }
    } else if (!activeIsLoading) {
      // If we're not on a dynamic tab page, set active tab to empty
      // But only if we're not in the middle of loading
      setActiveTab("");
    }
  }, [location.pathname, dynamicTabs, activeIsLoading, maintainingTab]);

  // Function to add a new tab
  const addTab = (id, title, path) => {
    // If path is provided explicitly, use it, otherwise construct it based on view mode
    let tabPath = path;

    // Handle financial model tab (different URL structure)
    if (id === 'financial-model') {
      if (!path) {
        if (isPortfolioMode && currentPortfolioName) {
          tabPath = `/portfolio/${encodeURIComponent(currentPortfolioName)}/financial-model`;
        } else if (!isPortfolioMode && currentDealName) {
          tabPath = `/deals/${encodeURIComponent(currentDealName)}/financial-model`;
        } else {
          tabPath = `/financial-model`;
        }
      }
      // If path is explicitly provided, use it as-is (already set above)
    } else {
      // Determine if this is a finance or competition tab based on the path or id
      let chartType = "finance-charts"; // default
      if (
        path?.includes("/competition-charts/") ||
        id?.includes("competition") ||
        id?.includes("market")
      ) {
        chartType = "competition-charts";
      }

      // If no explicit path provided, construct based on view mode
      if (!path) {
        if (isPortfolioMode && currentPortfolioName) {
          tabPath = `/portfolio/${encodeURIComponent(
            currentPortfolioName
          )}/${chartType}/${id}`;
        } else if (!isPortfolioMode && currentDealName) {
          tabPath = `/deals/${encodeURIComponent(
            currentDealName
          )}/${chartType}/${id}`;
        } else {
          // Fallback to old path format if no entity name is available
          tabPath = `/${chartType}/${id}`;
        }
      } else if (
        !path?.includes("/deals/") &&
        !path?.includes("/portfolio/") &&
        activeCurrentName
      ) {
        // If path doesn't include entity routing and we have an active entity, update it
        if (isPortfolioMode && currentPortfolioName) {
          tabPath = `/portfolio/${encodeURIComponent(
            currentPortfolioName
          )}/${chartType}/${id}`;
        } else if (!isPortfolioMode && currentDealName) {
          tabPath = `/deals/${encodeURIComponent(
            currentDealName
          )}/${chartType}/${id}`;
        }
      }
    }

    // Check if tab already exists
    if (!dynamicTabs.find((tab) => tab.id === id)) {
      setDynamicTabs((prev) => [...prev, { id, title, path: tabPath }]);
    }

    // Set as active tab and navigate to it
    setActiveTab(id);
    navigate(tabPath);
  };

  // Function to close a tab
  const closeTab = (id, e) => {
    // Prevent the click from also activating the tab
    if (e) e.stopPropagation();

    // Remove the tab from our list
    setDynamicTabs((prev) => prev.filter((tab) => tab.id !== id));

    // If we're closing the active tab, navigate to the main page
    if (activeTab === id) {
      if (isPortfolioMode) {
        navigate(
          currentPortfolioName
            ? `/portfolio/${encodeURIComponent(currentPortfolioName)}`
            : "/portfolio"
        );
      } else {
        navigate(
          currentDealName
            ? `/deals/${encodeURIComponent(currentDealName)}`
            : "/deals"
        );
      }
      setActiveTab("");
    }
  };

  // Function to switch to a tab
  const switchToTab = (id) => {
    const tab = dynamicTabs.find((tab) => tab.id === id);
    if (tab) {
      setActiveTab(id);
      navigate(tab.path);
    }
  };

  // Value object to be provided by the context
  const value = {
    dynamicTabs,
    activeTab,
    addTab,
    closeTab,
    switchToTab,
    maintainingTab,
  };

  return <TabContext.Provider value={value}>{children}</TabContext.Provider>;
};
